import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Pill, Drop, Bed, Tooth, PersonSimpleRun, Warning } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const SupplementsGuidePage = () => {
  const navigate = useNavigate();
  const [selectedSection, setSelectedSection] = useState(null);

  const sections = [
    {
      id: 'basics',
      emoji: '💊',
      title: 'Basic Supplements',
      subtitle: 'Support, not protection',
      icon: Pill,
      color: 'from-purple-500 to-pink-500',
      content: {
        intro: "Let's be honest — there isn't a lot of clear, public harm-reduction info about meth/amphetamines. This guide is harm reduction for folks who are already using. No supplement makes stimulant use \"safe.\" These are to support, not protect.",
        warning: "This is NOT medical advice. It's based on lived experience, peer knowledge, and what clinicians who work with stimulant users often share.",
        sections: [
          {
            title: 'Common Basics',
            items: [
              'Vitamin C — Antioxidant support',
              'Vitamin D — Mood and immune support',
              'B-Complex — Energy and nervous system',
              'Magnesium (glycinate or threonate) — Muscle relaxation, anxiety reduction, gentler on stomach',
              'Zinc — Immune support',
              'Omega-3 (EPA/DHA) — Brain health',
              'NAC — Liver and brain support',
              'CoQ10 — Heart and energy support',
              'L-Theanine — Calming without drowsiness',
              'Ashwagandha — Stress support',
              'Potassium — Electrolyte balance'
            ]
          },
          {
            title: 'For Sleep (if needed)',
            items: [
              'Melatonin — Sleep regulation',
              'L-Tryptophan — Natural sleep support'
            ]
          },
          {
            title: '⚠️ Important to Remember',
            danger: true,
            items: [
              'Check interactions with meds',
              'If you have health conditions, talk to a provider you trust',
              'Multivitamins often don\'t absorb well for some people'
            ]
          }
        ]
      }
    },
    {
      id: 'advanced',
      emoji: '🧪',
      title: 'Advanced Options',
      subtitle: 'For breaks or quitting',
      icon: Warning,
      color: 'from-orange-500 to-red-500',
      content: {
        intro: "Some people use these when taking breaks or quitting. These are more experimental and have more risks.",
        sections: [
          {
            title: 'What Some People Try',
            items: [
              'L-Tyrosine — Dopamine precursor',
              '5-HTP — Serotonin support',
              'Phenibut — Anxiety reduction (caution)',
              'Racetams — "Brain boosters"'
            ]
          },
          {
            title: '⚠️ Warnings',
            danger: true,
            items: [
              'Don\'t use these WHILE using stimulants — can make you feel tired or worse',
              'Can cause anxiety, heart strain, serotonin issues, or dependence',
              'Phenibut can be addictive and has rough withdrawals',
              '"Brain boosters" are not regulated and vary in quality',
              'If experimenting, go slow, don\'t stack a bunch at once, and listen to your body'
            ]
          }
        ]
      }
    },
    {
      id: 'hydration',
      emoji: '💧',
      title: 'Hydration',
      subtitle: 'More important than supplements',
      icon: Drop,
      color: 'from-cyan-500 to-blue-500',
      content: {
        intro: "Hydration matters MORE than supplements. Your body needs water to function, especially when using stimulants.",
        sections: [
          {
            title: 'Hydration Tips',
            items: [
              'Drink water regularly — don\'t wait until you\'re thirsty',
              'Add electrolytes sometimes (sports drinks, coconut water)',
              'Avoid ONLY sugary drinks',
              'Sip, don\'t chug — your body absorbs better slowly'
            ]
          }
        ]
      }
    },
    {
      id: 'food',
      emoji: '🍎',
      title: 'Food',
      subtitle: 'Fuel for your brain and body',
      icon: Pill,
      color: 'from-green-500 to-emerald-500',
      content: {
        intro: "Food is medicine. Even if you don't feel hungry, your body needs fuel.",
        sections: [
          {
            title: 'Eating Tips',
            items: [
              'Try to eat real food: protein, fats, fruits, veggies',
              'If you can\'t eat solid food: smoothies or protein shakes help',
              'Sugar + soda + no brushing = tooth damage',
              '"Meth mouth" is about dry mouth + sugar + not cleaning teeth'
            ]
          }
        ]
      }
    },
    {
      id: 'sleep',
      emoji: '😴',
      title: 'Sleep',
      subtitle: 'Non-negotiable for your brain',
      icon: Bed,
      color: 'from-indigo-500 to-purple-500',
      content: {
        intro: "Sleep is non-negotiable for your brain. No supplement replaces real rest.",
        sections: [
          {
            title: 'Sleep Tips',
            items: [
              'No sleep = more paranoia, crashes, and mental health spirals',
              'Even naps help',
              'Try calming options first: magnesium, L-theanine, melatonin',
              'Mixing downers to force sleep can be risky',
              'DON\'T take G to sleep!'
            ]
          }
        ]
      }
    },
    {
      id: 'hygiene',
      emoji: '🦷',
      title: 'Hygiene & Teeth',
      subtitle: 'Protect your smile',
      icon: Tooth,
      color: 'from-pink-500 to-rose-500',
      content: {
        intro: "Simple care = long-term protection. Your teeth and body deserve care.",
        sections: [
          {
            title: 'Hygiene Tips',
            items: [
              'Brush teeth 2x/day (soft brush)',
              'Rinse mouth after smoking',
              'Stay hydrated — dry mouth = more tooth damage',
              'Shower, wash hands, wash face',
              'Sugar-free gum helps with saliva production'
            ]
          }
        ]
      }
    },
    {
      id: 'movement',
      emoji: '🏃',
      title: 'Movement & Posture',
      subtitle: 'Reset your nervous system',
      icon: PersonSimpleRun,
      color: 'from-teal-500 to-cyan-500',
      content: {
        intro: "Gentle movement resets your nervous system and helps your body recover.",
        sections: [
          {
            title: 'Movement Tips',
            items: [
              'Stretch regularly',
              'Change positions — don\'t lock into one position for hours',
              'Breathe deeply',
              'Even 2 minutes of walking or dancing helps',
              'Your body needs to move to heal'
            ]
          }
        ]
      }
    }
  ];

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => selectedSection ? setSelectedSection(null) : navigate('/')}
              data-testid="back-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <div className="flex items-center space-x-2">
              <span className="text-3xl emoji-bounce">💊</span>
              <h1 className="text-2xl font-bold text-gradient">
                {selectedSection ? sections.find(s => s.id === selectedSection)?.title : 'Supplements & Self-Care'}
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        {!selectedSection ? (
          <>
            {/* Hero */}
            <div className="mb-8 p-6 party-gradient rounded-3xl text-white text-center">
              <div className="text-5xl mb-3">✨</div>
              <h2 className="text-2xl font-bold mb-3">Supplements & Self-Care</h2>
              <p className="text-white/90 leading-relaxed">
                For people who already use stimulants. Hydration, food, sleep, and hygiene matter MORE than any supplement. 💜
              </p>
            </div>

            {/* Priority reminder */}
            <div className="mb-6 p-4 bg-[rgb(var(--warning))]/10 border border-[rgb(var(--warning))]/30 rounded-2xl">
              <p className="text-sm text-[rgb(var(--text-secondary))] text-center">
                <strong className="text-[rgb(var(--warning))]">Remember:</strong> Hydration, food, sleep & hygiene matter MORE than supplements
              </p>
            </div>

            {/* Sections */}
            <div className="space-y-4">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setSelectedSection(section.id)}
                    data-testid={`section-${section.id}`}
                    className="w-full group relative bg-[rgb(var(--surface))] hover:bg-[rgb(var(--surface-elevated))] rounded-3xl p-6 border border-white/10 text-left transition-all duration-200 hover:border-[rgb(var(--primary))]/50 btn-hover overflow-hidden"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${section.color} opacity-0 group-hover:opacity-10 transition-opacity`}></div>
                    <div className="relative z-10 flex items-start">
                      <div className="text-4xl mr-4">{section.emoji}</div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-[rgb(var(--text-primary))] mb-1">
                          {section.title}
                        </h3>
                        <p className="text-sm text-[rgb(var(--text-secondary))]">
                          {section.subtitle}
                        </p>
                      </div>
                      <Icon size={24} weight="duotone" className="text-[rgb(var(--primary))] group-hover:scale-110 transition-transform" />
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div className="mt-8 p-6 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 rounded-2xl border border-[rgb(var(--primary))]/20 text-center">
              <p className="text-[rgb(var(--text-primary))] font-bold mb-2">
                This info is for folks who are already using.
              </p>
              <p className="text-sm text-[rgb(var(--text-secondary))]">
                If you don't use stimulants, you're not missing out. If you do use, you deserve honest info.
                <br/><strong>Harm reduction = meeting yourself where you are.</strong>
              </p>
            </div>
          </>
        ) : (
          <div className="space-y-6">
            {sections.find(s => s.id === selectedSection)?.content && (() => {
              const section = sections.find(s => s.id === selectedSection);
              const content = section.content;
              
              return (
                <>
                  {/* Intro */}
                  <div className={`p-6 bg-gradient-to-br ${section.color} rounded-3xl text-white`}>
                    <p className="leading-relaxed">{content.intro}</p>
                  </div>

                  {/* Warning */}
                  {content.warning && (
                    <div className="p-4 bg-[rgb(var(--warning))]/10 border border-[rgb(var(--warning))]/30 rounded-2xl">
                      <p className="text-sm text-[rgb(var(--text-secondary))]">
                        <strong className="text-[rgb(var(--warning))]">⚠️</strong> {content.warning}
                      </p>
                    </div>
                  )}

                  {/* Sections */}
                  {content.sections.map((sect, idx) => (
                    <div 
                      key={idx} 
                      className={`rounded-2xl p-5 border ${sect.danger ? 'bg-[rgb(var(--danger))]/10 border-[rgb(var(--danger))]/30' : 'bg-[rgb(var(--surface))] border-white/5'}`}
                    >
                      <h3 className={`text-lg font-bold mb-3 ${sect.danger ? 'text-[rgb(var(--danger))]' : 'text-[rgb(var(--text-primary))]'}`}>
                        {sect.title}
                      </h3>
                      {sect.items && (
                        <ul className="space-y-2">
                          {sect.items.map((item, i) => (
                            <li key={i} className="text-sm text-[rgb(var(--text-secondary))] flex items-start">
                              <span className={`mr-2 ${sect.danger ? 'text-[rgb(var(--danger))]' : 'text-[rgb(var(--primary))]'}`}>•</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </>
              );
            })()}
          </div>
        )}
      </main>
    </div>
  );
};

export default SupplementsGuidePage;
